import type {
  CalEvent,
  EventInput,
  Template,
  TemplateInput,
  UploadFile,
  UploadResult,
  UserInfo,
} from '../shared/types';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  unauthorizedHandler = fn;
}

const NO_401_HANDLING = ['/login', '/me', '/setup-password'];

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(0, 'Server nicht erreichbar.');
  }
  if (!res.ok) {
    let msg = res.statusText || `Fehler ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      /* keine JSON-Antwort */
    }
    if (res.status === 401 && !NO_401_HANDLING.includes(path) && unauthorizedHandler) unauthorizedHandler();
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

export const api = {
  users: () => req<UserInfo[]>('GET', '/users'),
  me: () => req<{ name: string }>('GET', '/me'),
  login: (name: string, password: string) => req<{ name: string }>('POST', '/login', { name, password }),
  setupPassword: (name: string, password: string) =>
    req<{ name: string }>('POST', '/setup-password', { name, password }),
  logout: () => req<{ ok: true }>('POST', '/logout'),
  changePassword: (password: string) => req<{ ok: true }>('POST', '/password', { password }),
  config: () => req<{ googleClientId: string; calendarName: string }>('GET', '/config'),

  templates: () => req<Template[]>('GET', '/templates'),
  createTemplate: (t: TemplateInput) => req<Template>('POST', '/templates', t),
  updateTemplate: (id: string, t: TemplateInput) => req<Template>('PUT', `/templates/${encodeURIComponent(id)}`, t),
  deleteTemplate: (id: string) => req<{ ok: true }>('DELETE', `/templates/${encodeURIComponent(id)}`),

  events: () => req<CalEvent[]>('GET', '/events'),
  createEvent: (e: EventInput) => req<CalEvent>('POST', '/events', e),
  updateEvent: (id: string, e: EventInput) => req<CalEvent>('PUT', `/events/${encodeURIComponent(id)}`, e),
  deleteEvent: (id: string) => req<{ ok: true }>('DELETE', `/events/${encodeURIComponent(id)}`),

  upload: (files: UploadFile[]) => req<UploadResult>('POST', '/upload', { files }),
  tombstones: () => req<string[]>('GET', '/tombstones'),
  clearTombstones: () => req<{ ok: true }>('POST', '/tombstones/clear'),
};
